import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded'
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'

function getRemainingSeconds(targetTime, nowTs) {
  if (!targetTime) return null
  const targetMs = new Date(targetTime).getTime()
  if (Number.isNaN(targetMs)) return null
  return Math.max(0, Math.ceil((targetMs - nowTs) / 1000))
}

function formatCountdown(seconds) {
  if (seconds == null) return '--:--'
  const safeSeconds = Math.max(0, seconds)
  const minutesPart = Math.floor(safeSeconds / 60)
  const secondsPart = safeSeconds % 60
  return `${String(minutesPart).padStart(2, '0')}:${String(secondsPart).padStart(2, '0')}`
}

export default function CheckinRequestStatusModal({
  open,
  requestData,
  nowTs,
  onClose,
  onOpenRequestedRoom,
}) {
  if (!requestData) {
    return null
  }

  const occupantName = requestData?.occupyingPractitionerName || 'current mentor'
  const roomLabel = [requestData?.clinicBrandName, requestData?.clinicLocationName, requestData?.chamberRoomTitle]
    .filter(Boolean)
    .join(' > ')
  const pendingSeconds = getRemainingSeconds(requestData?.expiresAt, nowTs)
  const checkoutSeconds = getRemainingSeconds(requestData?.countdownEndsAt, nowTs)
  const isCountdown = requestData?.status === 'CHECKOUT_PENDING'
  const isDenied = requestData?.status === 'DENIED'
  const isRoomAvailable = requestData?.roomAvailable === true || requestData?.status === 'ROOM_AVAILABLE'

  const title = isRoomAvailable
    ? 'Room Available'
    : isDenied
    ? 'Request Declined'
    : isCountdown
    ? 'Checkout Underway'
    : 'Waiting for Checkout'

  const description = isRoomAvailable
    ? `${roomLabel || 'This room'} is now available. You can open the clinic and check in.`
    : isDenied
    ? `${occupantName} chose to stay checked in for now.`
    : isCountdown
    ? `${occupantName} is being checked out now. The chamber should free up in a few seconds.`
    : `${occupantName} has up to 2 minutes to respond before the checkout countdown starts automatically.`

  const countdownLabel = isCountdown
    ? `Checkout in ${formatCountdown(checkoutSeconds)}`
    : !isDenied && !isRoomAvailable
    ? `Response window ${formatCountdown(pendingSeconds)}`
    : null

  return (
    <Dialog
      open={open}
      onClose={isDenied || isRoomAvailable ? onClose : undefined}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: '12px !important' }}>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2.5,
              border: '1px solid rgba(37, 99, 235, 0.12)',
              background:
                'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(255, 255, 255, 0.98) 100%)',
              display: 'grid',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MeetingRoomRoundedIcon sx={{ color: 'rgba(37, 99, 235, 0.9)', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {roomLabel || 'Requested chamber room'}
              </Typography>
            </Box>
            {requestData?.reason ? (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Note: {requestData.reason}
              </Typography>
            ) : null}
          </Box>

          <Typography variant="body1" sx={{ color: 'text.primary' }}>
            {description}
          </Typography>

          {countdownLabel ? (
            <Chip
              icon={isCountdown ? <WarningAmberRoundedIcon /> : <HourglassTopRoundedIcon />}
              label={countdownLabel}
              sx={{
                width: 'fit-content',
                backgroundColor: isCountdown ? 'rgba(225, 29, 72, 0.12)' : 'rgba(37, 99, 235, 0.1)',
                color: isCountdown ? 'rgba(190, 24, 93, 0.96)' : 'rgba(37, 99, 235, 0.96)',
                border: `1px solid ${
                  isCountdown ? 'rgba(225, 29, 72, 0.16)' : 'rgba(37, 99, 235, 0.16)'
                }`,
                fontWeight: 700,
                '& .MuiChip-icon': {
                  color: 'inherit',
                },
              }}
            />
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
        {(isDenied || isRoomAvailable) && (
          <Button
            onClick={onClose}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: 'none', borderRadius: '999px' }}
          >
            Dismiss
          </Button>
        )}
        {isRoomAvailable && (
          <Button
            onClick={onOpenRequestedRoom}
            variant="contained"
            startIcon={<CheckCircleRoundedIcon />}
            sx={{ textTransform: 'none', borderRadius: '999px', px: 2.25 }}
          >
            Open clinic
          </Button>
        )}
        {!isDenied && !isRoomAvailable && (
          <Typography variant="caption" sx={{ color: 'text.secondary', px: 1 }}>
            This will update automatically.
          </Typography>
        )}
      </DialogActions>
    </Dialog>
  )
}
